<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Security Guards"
      subtitle="Manage society security guards and their access"
      add-label="Add"
      :show-edit="true"
      :show-delete="true"
      @add="openGuardAdd"
      @edit="openGuardEdit($event)"
      @delete="confirmDeleteDialog($event.id)"
      :columns="columns"
      :items="securityguards"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
      @update:options="onOptionsUpdate"
    >
      <!-- Custom Cells -->
      <template #col-profile_url="{ item }">
        <div class="profile-cell">
          <div class="p-avatar-wrap">
            <img
              v-if="item.profile_url && item.profile_url !== 'null'"
              :src="API_BASE_URL + '/uploads/' + item.profile_url"
              class="p-avatar-img"
              style="cursor: pointer"
              @click.stop="openPhoto(API_BASE_URL + '/uploads/' + item.profile_url, item.name)"
            />
            <div
              v-else
              class="p-avatar-placeholder"
              :style="{ background: primaryColor + '20', color: primaryColor }"
            >
              {{
                item.name && item.name !== "null"
                  ? item.name.charAt(0).toUpperCase()
                  : "G"
              }}
            </div>
          </div>
          <div class="p-info">
            <span class="p-name">{{
              item.name && item.name !== "null" ? item.name : "—"
            }}</span>
            <!-- <span class="p-id" v-if="item.id">ID: #SG{{ item.id }}</span> -->
          </div>
        </div>
      </template>

      <template #col-mobile_no="{ item }">
        <div class="mobile-cell">
          <v-icon size="14" color="#94A3B8" class="mr-1"
            >mdi-phone-outline</v-icon
          >
          <span
            >{{
              item.country_code
                ? (item.country_code.toString().startsWith("+")
                    ? item.country_code
                    : "+" + item.country_code) + " "
                : ""
            }}{{ item.mobile_no }}</span
          >
        </div>
      </template>

      <template #col-email="{ item }">
        <div class="email-cell" v-if="item.email">
          <v-icon size="14" color="#94A3B8" class="mr-1"
            >mdi-email-outline</v-icon
          >
          <span>{{ item.email }}</span>
        </div>
        <span class="dash" v-else>—</span>
      </template>
    </PremiumTable>

    <!-- ── Add / Edit Modal ────────────────── -->
    <PremiumModal
      v-model="guardDialog"
      :title="(guard.id ? 'Edit' : 'Add') + ' Security Guard'"
      :loading="saveLoading"
      submit-label="Submit"
      max-width="500"
      @submit="guard.id ? updateGuard() : saveGuard()"
      @close="closeGuardDialog"
    >
      <!-- Avatar uploader -->
      <div class="d-flex justify-center mb-6">
        <input
          type="file"
          ref="guard_pic"
          @change="changeFile($event)"
          accept="image/*"
          hidden
        />
        <div class="avatar-uploader-container">
          <v-avatar size="100" class="elevation-3 guard-avatar">
            <img
              v-if="previewImage || guard.profile_url"
              :src="
                previewImage || API_BASE_URL + '/uploads/' + guard.profile_url
              "
              style="object-fit: contain"
            />
            <span
              v-else
              class="text-h3 font-weight-bold"
              :style="`color:${primaryColor}`"
            >
              {{ guard.name ? guard.name.charAt(0).toUpperCase() : "G" }}
            </span>
          </v-avatar>
          <v-btn
            fab
            small
            depressed
            @click="openFileExplorer"
            class="avatar-edit-btn"
            color="var(--brand)"
          >
            <v-icon small color="white">mdi-camera</v-icon>
          </v-btn>
        </div>
      </div>

      <v-row dense>
        <!-- Name -->
        <v-col cols="12">
          <PremiumInput
            v-model="guard.name"
            label="Full Name"
            placeholder="Enter guard's full name"
            icon="mdi-account-outline"
            v-validate="'required'"
            name="guard_name"
            data-vv-as="Name"
            :error="errors.first('guard_name') || serverSideErrors.name"
          />
        </v-col>

        <!-- Mobile (country code + number inline) -->
        <v-col cols="12">
          <div class="field-label mb-2">Mobile Number</div>
          <div class="phone-row">
            <div class="country-code-wrap">
              <vue-country-code
                @onSelect="onSelect"
                class="custom-country-code-select-full"
                :defaultCountry="guard.countryIso || 'IN'"
              />
            </div>
            <div class="mobile-input-wrap">
              <PremiumInput
                v-model="guard.mobile_no"
                label=""
                placeholder="Enter mobile number"
                icon="mdi-phone-outline"
                v-validate="'required'"
                name="guard_mobile"
                data-vv-as="Mobile"
                :error="
                  errors.first('guard_mobile') || serverSideErrors.mobileNo
                "
              />
            </div>
          </div>
        </v-col>

        <!-- Email -->
        <v-col cols="12">
          <PremiumInput
            v-model="guard.email"
            label="Email Address"
            placeholder="Enter email (optional)"
            icon="mdi-email-outline"
            name="guard_email"
            :error="serverSideErrors.email"
          />
        </v-col>
      </v-row>
    </PremiumModal>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Security Guard"
      message="Are you sure you want to remove this security guard? Their login access will be revoked immediately."
      confirm-text="Yes, Remove Guard"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteSecurityGuard"
    />

    <!-- ── Media Viewer ────────────────────── -->
    <MediaViewer
      v-model="mediaViewer.open"
      :items="mediaViewer.items"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapMutations, mapState } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumModal from "../../common/PremiumModal.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import MediaViewer from "../../common/MediaViewer.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "SecurityGuardList",
  mixins: [filterMixin],
  components: { PremiumTable, PremiumModal, PremiumInput, ConfirmModal, MediaViewer },

  data() {
    return {
      search: "",
      securityguards: [],
      totalRecords: 0,
      loading: false,
      saveLoading: false,
      deleteLoading: false,
      deleteDialog: false,
      guardDialog: false,
      delete_id: "",
      page: 1,
      itemsPerPage: 10,
      options: {
        sortBy: ["created_at"],
        sortDesc: [true],
      },
      user: {},
      // Guard form
      guard: {
        id: null,
        name: "",
        mobile_no: "",
        email: "",
        country_code: "+91",
        countryIso: "IN",
        profile_url: null,
      },
      profile_pic: null,
      previewImage: null,
      serverSideErrors: {},
      mediaViewer: { open: false, items: [] },
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    columns() {
      return [
        {
          key: "profile_url",
          label: this.$t("securityguard.headers.name") || "Guard Profile",
          width: "300px",
        },
        {
          key: "mobile_no",
          label: this.$t("securityguard.headers.mobile_no") || "Contact",
        },
        {
          key: "email",
          label: this.$t("securityguard.headers.email") || "Email",
        },
      ];
    },
  },

  watch: {
    page() {
      this.getSecurityGuards();
    },
    itemsPerPage() {
      this.getSecurityGuards();
    },
    search() {
      this.page = 1;
      this.getSecurityGuards();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
    },

    onOptionsUpdate(opt) {
      this.options.sortBy = opt.sortBy || ["created_at"];
      this.options.sortDesc = opt.sortDesc || [true];
      this.getSecurityGuards();
    },

    onSelect(event) {
      this.guard.country_code = "+" + event.dialCode;
      this.guard.countryIso = event.iso2;
    },

    openFileExplorer() {
      this.$refs.guard_pic.click();
    },

    changeFile(e) {
      this.profile_pic = e.target.files[0];
      const input = this.$refs.guard_pic;
      const file = input.files;
      if (file && file[0]) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          this.previewImage = ev.target.result;
        };
        reader.readAsDataURL(file[0]);
      }
    },

    openGuardAdd() {
      this.guard = {
        id: null,
        name: "",
        mobile_no: "",
        email: "",
        country_code: "+91",
        countryIso: "IN",
        profile_url: null,
      };
      this.profile_pic = null;
      this.previewImage = null;
      this.$validator.reset();
      this.serverSideErrors = {};
      this.guardDialog = true;
    },

    openGuardEdit(item) {
      this.guard = {
        id: item.id,
        name: item.name || "",
        mobile_no: item.mobile_no || "",
        email: item.email || "",
        country_code: item.country_code || "",
        countryIso: item.countryIso || "IN",
        profile_url: item.profile_url || null,
      };
      this.profile_pic = null;
      this.previewImage = null;
      this.$validator.reset();
      this.serverSideErrors = {};
      this.guardDialog = true;
    },

    saveGuard() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        this.saveLoading = true;
        const formData = new FormData();
        formData.append("name", this.guard.name);
        formData.append("mobileNo", this.guard.mobile_no);
        formData.append("email", this.guard.email || "");
        formData.append("country_code", this.guard.country_code);
        formData.append("countryIso", this.guard.countryIso);
        if (this.profile_pic) formData.append("profile_pic", this.profile_pic);

        this.$axios
          .post("/societyadmin/securityguard/", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((response) => {
            this.saveLoading = false;
            const data = response.data;
            if (data.status) {
              this.setAlert({
                showAlert: true,
                message: data.message,
                variant: "green accent-4",
                timeout: 2000,
              });
              this.getSecurityGuards();
              this.closeGuardDialog();
            } else {
              this.handleErrors(data);
            }
          })
          .catch(() => {
            this.saveLoading = false;
          });
      });
    },

    updateGuard() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        this.saveLoading = true;
        const formData = new FormData();
        formData.append("name", this.guard.name);
        formData.append("mobileNo", this.guard.mobile_no);
        formData.append("email", this.guard.email || "");
        formData.append("country_code", this.guard.country_code);
        formData.append("countryIso", this.guard.countryIso);
        if (this.profile_pic) formData.append("profile_pic", this.profile_pic);

        this.$axios
          .put("/societyadmin/securityguard/" + this.guard.id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then((response) => {
            this.saveLoading = false;
            const data = response.data;
            if (data.status) {
              this.setAlert({
                showAlert: true,
                message: data.message,
                variant: "green accent-4",
                timeout: 2000,
              });
              this.getSecurityGuards();
              this.closeGuardDialog();
            } else {
              this.handleErrors(data);
            }
          })
          .catch(() => {
            this.saveLoading = false;
          });
      });
    },

    closeGuardDialog() {
      this.guardDialog = false;
      this.guard = {
        id: null,
        name: "",
        mobile_no: "",
        email: "",
        country_code: "+91",
        countryIso: "IN",
        profile_url: null,
      };
      this.profile_pic = null;
      this.previewImage = null;
      this.serverSideErrors = {};
      this.$validator.reset();
    },

    getSecurityGuards() {
      this.loading = true;
      const sortBy = this.options.sortBy[0] || "";
      const sortDesc = this.options.sortDesc[0] || "";

      const params = [
        `society_id=${this.user.id}`,
        `page=${this.page}`,
        `perPage=${this.itemsPerPage}`,
        `sortBy=${sortBy}`,
        `sortDesc=${sortDesc}`,
        `search=${this.search || ""}`,
      ].join("&");

      this.$axios
        .get("societyadmin/securityguard?" + params)
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.securityguards =
              data.securityguards.data || data.securityguards || [];
            this.totalRecords =
              data.total ||
              (data.securityguards && data.securityguards.total) ||
              0;
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    confirmDeleteDialog(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteSecurityGuard() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/securityguard/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getSecurityGuards();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },

    handleErrors(data) {
      if (data.errors) {
        this.serverSideErrors = {};
        data.errors.forEach((e) => {
          this.serverSideErrors[e.field] = e.message;
        });
      } else {
        this.showError(data.message);
      }
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },

    openPhoto(url, name) {
      this.mediaViewer.items = [{ url, type: "image", name: name || "Photo" }];
      this.mediaViewer.open = true;
    },

    debounceSearch: debounce(function () {
      this.page = 1;
      this.getSecurityGuards();
    }, 500),
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};

    // Initialize persistent filters
    this.initFilters("securityguard");

    this.getSecurityGuards();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Table Cells ── */
.profile-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.p-avatar-wrap {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--surface2);
  border: 1px solid var(--border);
}
.p-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.p-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
}
.p-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.p-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
}
.p-id {
  font-size: 10px;
  font-weight: 700;
  color: var(--tx4, #94a3b8);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.mobile-cell {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2);
}
.email-cell {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--tx3);
}

/* ── Phone Row (inline country code + mobile) ── */
.phone-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.country-code-wrap {
  flex-shrink: 0;
  width: 90px;
}
.mobile-input-wrap {
  flex: 1;
  min-width: 0;
}

/* ── Avatar Uploader ── */
.avatar-uploader-container {
  position: relative;
  display: inline-block;
}
.guard-avatar {
  background: var(--surface2) !important;
  border: 2px solid var(--border, rgba(0, 0, 0, 0.08)) !important;
}
.avatar-edit-btn {
  position: absolute !important;
  bottom: 0px !important;
  right: 0px !important;
  width: 32px !important;
  height: 32px !important;
  border: 3px solid white !important;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15) !important;
}
.theme--dark .avatar-edit-btn {
  border-color: #111827 !important;
}

/* ── Country Code Styles ── */
.field-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding-left: 4px;
}
.custom-country-code-select-full {
  width: 100%;
  height: 40px;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1)) !important;
  background-color: var(--surface2) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}
.custom-country-code-select-full:hover {
  border-color: var(--brand) !important;
}
.theme--dark .custom-country-code-select-full {
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* dropdown opens upward when near bottom of modal */
::v-deep .custom-country-code-select-full .dropdown-list {
  width: 260px !important;
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 4px;
  max-height: 200px;
  overflow-y: auto;
}
</style>
